/**
 * Food Court / Restaurant POS  (Screen ID: 6.16.14)
 * ─────────────────────────────────────────────────────────────────────────
 * EXACT pixel-perfect implementation of pos-foodcourt-workspaces.html
 * Same CSS variables, same component structure, same 3-step product flow:
 *   Step 1 → Service Type (Dine In / Take Away / Special)
 *   Step 2 → Table Map (only for Dine In)
 *   Step 3 → Item selection (categories + tiles)
 * Industry: Food Court, Restaurant, Café, QSR
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './FoodCourtPOS.css';

// ─── Data ─────────────────────────────────────────────────────────────────
const FOOD_CATEGORIES = {
  Starters: [
    {n:'Chicken Biryani',  p:450, e:'🍛'}, {n:'Egg Plant Kebab',  p:280, e:'🍢'},
    {n:'Greek Salad',      p:220, e:'🥗'}, {n:'Paneer Tikka',     p:310, e:'🧀'},
    {n:'Spring Rolls',     p:190, e:'🥟'}, {n:'Chicken Wings',    p:340, e:'🍗'},
    {n:'Nachos Supreme',   p:260, e:'🌮'}, {n:'Fish Fingers',     p:380, e:'🐟'},
    {n:'Onion Rings',      p:150, e:'🧅'}, {n:'Hummus Platter',   p:230, e:'🥙'},
    {n:'Mushroom Toast',   p:200, e:'🍄'}, {n:'Corn Chaat',       p:170, e:'🌽'},
    {n:'Prawn Tempura',    p:420, e:'🍤'}, {n:'Falafel Bites',    p:210, e:'🧆'},
    {n:'Loaded Fries',     p:240, e:'🍟'},
  ],
  Beverages: [
    {n:'Espresso',         p:120, e:'☕'}, {n:'Lemon With Mint',  p:150, e:'🍹'},
    {n:'Mojito Cocktail',  p:320, e:'🍸'}, {n:'Orange Juice',     p:140, e:'🧃'},
    {n:'Cappuccino',       p:160, e:'☕'}, {n:'Iced Tea',          p:130, e:'🧉'},
    {n:'Mango Smoothie',   p:210, e:'🥤'}, {n:'Cold Coffee',       p:180, e:'🥶'},
    {n:'Watermelon Cooler',p:170, e:'🍉'}, {n:'Hot Chocolate',     p:190, e:'🍫'},
    {n:'Masala Chai',      p:90,  e:'🍵'}, {n:'Sparkling Water',   p:80,  e:'💧'},
    {n:'Virgin Pina Colada',p:250,e:'🍍'}, {n:'Buttermilk',        p:70,  e:'🥛'},
  ],
  Mains: [
    {n:'Chicken Biryani',  p:450, e:'🍛'}, {n:'Tandoori Chicken', p:520, e:'🍗'},
    {n:'Grilled Kebab',    p:390, e:'🍖'}, {n:'Mutton Rogan Josh',p:590, e:'🍲'},
    {n:'Butter Chicken',   p:460, e:'🍛'}, {n:'Veg Kolhapuri',    p:340, e:'🥘'},
    {n:'Paneer Butter Masala',p:360,e:'🧈'},{n:'Fish Curry',       p:480, e:'🐠'},
    {n:'Dal Makhani',      p:250, e:'🍲'}, {n:'Hyderabadi Biryani',p:470,e:'🍚'},
    {n:'Grilled Salmon',   p:650, e:'🐟'}, {n:'Chicken Chettinad',p:440, e:'🌶'},
    {n:'Vegetable Korma',  p:290, e:'🥕'}, {n:'Lamb Chops',       p:680, e:'🍖'},
  ],
  Desserts: [
    {n:'Gulab Jamun',      p:130, e:'🍮'}, {n:'Chocolate Brownie',p:180, e:'🍫'},
    {n:'Rasmalai',         p:150, e:'🥣'}, {n:'Ice Cream Sundae', p:210, e:'🍨'},
    {n:'Tiramisu',         p:260, e:'🍰'}, {n:'Cheesecake',       p:240, e:'🧁'},
    {n:'Kulfi',            p:120, e:'🍦'}, {n:'Apple Pie',        p:190, e:'🥧'},
  ],
  Combos: [
    {n:'Biryani + Drink Combo', p:600,  e:'🍱'},
    {n:'Family Feast Combo',    p:1450, e:'🍽'},
    {n:'Kids Meal Combo',       p:340,  e:'🧒'},
    {n:'Date Night Combo',      p:1200, e:'💑'},
    {n:'Office Lunch Combo',    p:520,  e:'💼'},
    {n:'Weekend Brunch Combo',  p:750,  e:'🥞'},
  ],
};
const FOOD_CATS = Object.keys(FOOD_CATEGORIES);

const TABLES = Array.from({length:18}, (_, i) => ({
  no:     i + 1,
  status: i % 5 === 0 ? 'occupied' : (i % 7 === 0 ? 'reserved' : 'available'),
}));

const PAY_METHODS = [
  {id:'cash',   ic:'💵', lb:'Cash'},
  {id:'card',   ic:'💳', lb:'Card'},
  {id:'upi',    ic:'📱', lb:'UPI'},
  {id:'wallet', ic:'👛', lb:'Wallet'},
  {id:'split',  ic:'➗', lb:'Split Bill'},
];

const PROMOS = [
  {code:'WELCOME10',  desc:'10% off on total bill for new guests',   pct:10,  flat:0  },
  {code:'HAPPYHOUR20',desc:'20% off beverages, 4–7 PM',              pct:20,  flat:0  },
  {code:'FEST50',     desc:'Flat ₹50 off on bills above ₹500',       pct:0,   flat:50 },
  {code:'COMBO15',    desc:'15% off on combo meals',                  pct:15,  flat:0  },
];

const CUSTOMERS = [
  {name:'Steephen',   mobile:'93960 93960', tier:'Gold'    },
  {name:'Anita Rao',  mobile:'98450 11223', tier:'Silver'  },
  {name:'Ravi Kumar', mobile:'90080 33445', tier:'Platinum'},
];

function fmt(n) { return (isNaN(n)?0:n).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function nowStr()   { return new Date().toLocaleTimeString('en-IN',{hour12:false}); }
function todayStr() { return new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }

export default function FoodCourtPOSScreen({ theme, navigateTo }) {
  // Product workspace steps: 'service' | 'table' | 'items'
  const [productStep,  setProductStep]  = useState('service');
  const [serviceType,  setServiceType]  = useState(null);   // 'DineIn'|'TakeAway'|'Special'
  const [selectedTable,setSelectedTable]= useState(null);
  const [activeCat,    setActiveCat]    = useState('Starters');
  const [searchQ,      setSearchQ]      = useState('');

  const [activeWs,     setActiveWs]     = useState('products');
  const [order,        setOrder]        = useState([]);
  const [custName,     setCustName]     = useState('Steephen');
  const [custMobile,   setCustMobile]   = useState('93960 93960');
  const [tableNo,      setTableNo]      = useState('');
  const [covered,      setCovered]      = useState('');
  const [payMethod,    setPayMethod]    = useState(null);
  const [tendered,     setTendered]     = useState('');
  const [promo,        setPromo]        = useState(null);
  const [custSearch,   setCustSearch]   = useState('');
  const [clock,        setClock]        = useState(nowStr());
  const [toastMsg,     setToastMsg]     = useState('');
  const [toastOn,      setToastOn]      = useState(false);
  const toastRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setClock(nowStr()), 1000);
    return () => clearInterval(t);
  }, []);

  const showToast = useCallback((msg) => {
    setToastMsg(msg); setToastOn(true);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToastOn(false), 1800);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'F10') { e.preventDefault(); handleSave(); }
      if (e.key === 'F2')  { e.preventDefault(); voidLast(); }
      if (e.key === 'F5')  { e.preventDefault(); showToast('Bill held (F5)'); }
      if (e.key === 'F1')  { e.preventDefault(); showToast('Recalling held bill (F1)'); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // Totals
  const totQty  = order.reduce((s,r) => s + r.qty, 0);
  const gross   = order.reduce((s,r) => s + r.qty * r.p, 0);
  const promoAmt= promo
    ? (promo.flat > 0 ? (gross > 500 ? promo.flat : 0) : gross * promo.pct / 100)
    : 0;
  const vatPct  = 5;
  const afterDisc = gross - promoAmt;
  const vat       = afterDisc * vatPct / 100;
  const inclVat   = afterDisc + vat;
  const rounded   = Math.round(inclVat);
  const roundOff  = rounded - inclVat;
  const change    = Math.max(0, (+tendered || 0) - rounded);

  function addToOrder(name, price) {
    setOrder(prev => {
      const idx = prev.findIndex(r => r.name === name);
      if (idx >= 0) {
        const u = [...prev];
        u[idx] = { ...u[idx], qty: u[idx].qty + 1 };
        return u;
      }
      return [...prev, { name, p: price, qty: 1 }];
    });
    showToast(name + ' added');
  }

  function voidLast() {
    if (!order.length) { showToast('No rows to void'); return; }
    setOrder(prev => prev.slice(0, -1));
    showToast('Last row voided (F2)');
  }

  function handleSave() {
    if (!order.length) { showToast('Add items before saving'); return; }
    showToast('Order saved — Total ₹' + fmt(rounded));
    setActiveWs('payment');
  }

  function handleClear() {
    setOrder([]); setPromo(null); setPayMethod(null);
    setTendered(''); setProductStep('service'); setServiceType(null); setSelectedTable(null);
    showToast('Order cleared');
  }

  function confirmPayment() {
    if (!payMethod) { showToast('Select a payment method'); return; }
    if (!tendered || +tendered < rounded) { showToast('Tendered amount is less than due'); return; }
    showToast('Payment confirmed via ' + payMethod.toUpperCase() + ' — Change ₹' + fmt(change));
    setTimeout(handleClear, 1600);
  }

  function goToStep(step) { setProductStep(step); }

  function selectServiceType(svc) {
    setServiceType(svc);
    if (svc === 'DineIn') { goToStep('table'); showToast('Dine In selected'); }
    else { goToStep('items'); showToast(svc === 'TakeAway' ? 'Take Away selected' : 'Special Section selected'); }
  }

  function selectTable(table) {
    if (table.status === 'occupied') { showToast('Table ' + table.no + ' is occupied'); return; }
    setTableNo(String(table.no));
    setSelectedTable(table.no);
    goToStep('items');
    showToast('Table ' + table.no + ' selected');
  }

  const svcBadge = serviceType === 'DineIn'
    ? 'Dine In · Table ' + (selectedTable || '?')
    : serviceType === 'TakeAway' ? 'Take Away' : 'Special Section';

  // Filtered tiles
  const tiles = (() => {
    const pool = FOOD_CATEGORIES[activeCat] || [];
    if (!searchQ.trim()) return pool;
    const q = searchQ.toLowerCase();
    return Object.values(FOOD_CATEGORIES).flat().filter(i => i.n.toLowerCase().includes(q));
  })();

  const filtCusts = custSearch.trim()
    ? CUSTOMERS.filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase()) || c.mobile.includes(custSearch))
    : CUSTOMERS;

  // Quick-amount options
  const quickAmts = [...new Set([rounded, Math.ceil(rounded/100)*100, Math.ceil(rounded/500)*500, Math.ceil(rounded/1000)*1000])];

  return (
    <div className="fpos-root" data-testid="foodcourt-pos-screen">
      <div className="fpos-main">

        {/* ════ LEFT: ORDER PANEL ════════════════════════════════════════ */}
        <div className="fpos-order-panel">
          <div className="fpos-order-head">
            <span>Item</span>
            <span className="fpos-qty">Qty</span>
            <span className="fpos-val">Value</span>
          </div>

          <div className="fpos-order-list">
            {order.length === 0
              ? <div className="fpos-order-empty">No items yet — select from Product Selection</div>
              : order.map((r, i) => (
                <div className="fpos-order-row" key={i}>
                  <span className="fpos-nm">{r.name}</span>
                  <span className="fpos-qty fpos-mono">{r.qty}</span>
                  <span className="fpos-val fpos-mono">
                    {fmt(r.qty * r.p)}
                    <button className="fpos-del"
                      onClick={() => setOrder(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </span>
                </div>
              ))
            }
          </div>

          <div className="fpos-order-total-line">
            <span id="orderQtyTotal" className="fpos-mono">{totQty}</span>
            <span id="orderValTotal" className="fpos-mono">{fmt(gross)}</span>
          </div>

          <div className="fpos-order-actions">
            {[
              {ic:'📌', lb:'Old Bill',      fn: () => showToast('Opening old bill…')},
              {ic:'🗑',  lb:'Row Void (F2)', fn: voidLast},
              {ic:'📁', lb:'Hold (F5)',     fn: () => showToast('Bill held (F5)')},
              {ic:'🗂',  lb:'Get Hold (F1)', fn: () => showToast('Recalling held bill (F1)')},
            ].map(b => (
              <div key={b.lb} className="fpos-oa-btn" onClick={b.fn}>
                <div className="fpos-oa-ic">{b.ic}</div>
                <div className="fpos-oa-lb">{b.lb}</div>
              </div>
            ))}
          </div>

          <div className="fpos-info-grid">
            <div className="fpos-info-item editable">
              <label>Name</label>
              <input value={custName} onChange={e => setCustName(e.target.value)} />
            </div>
            <div className="fpos-info-item">
              <label>Total Qty</label>
              <span className="fpos-mono">{fmt(totQty)}</span>
            </div>
            <div className="fpos-info-item editable">
              <label>Mobile No</label>
              <input className="fpos-mono" value={custMobile} onChange={e => setCustMobile(e.target.value)} />
            </div>
            <div className="fpos-info-item">
              <label>Excl VAT</label>
              <span className="fpos-mono">{fmt(afterDisc)}</span>
            </div>
            <div className="fpos-info-item">
              <label>Steward</label>
              <span>Stella</span>
            </div>
            <div className="fpos-info-item">
              <label>Incl VAT</label>
              <span className="fpos-mono">{fmt(inclVat)}</span>
            </div>
            <div className="fpos-info-item editable">
              <label>Table</label>
              <input value={tableNo} onChange={e => setTableNo(e.target.value)} style={{width:40}} />
            </div>
            <div className="fpos-info-item">
              <label>Gross Amount</label>
              <span className="fpos-mono">{fmt(gross)}</span>
            </div>
            <div className="fpos-info-item editable">
              <label>Table Covered</label>
              <input value={covered} onChange={e => setCovered(e.target.value)} style={{width:40}} />
            </div>
            <div className="fpos-info-item">
              <label>Round Off</label>
              <span className="fpos-mono">{roundOff.toFixed(2)}</span>
            </div>
          </div>

          <div className="fpos-total-bar">
            <span>Total Amount</span>
            <span>
              <span className="fpos-cur">INR</span>
              <span className="fpos-mono" data-testid="fc-grand-total">{fmt(rounded)}</span>
            </span>
          </div>
        </div>

        {/* ════ CENTER: WORKSPACE ════════════════════════════════════════ */}
        <div className="fpos-workspace">
          <div className="fpos-ws-switch">
            {[
              {ws:'products', ic:'🍽', lb:'Product Selection'},
              {ws:'payment',  ic:'💳', lb:'Payment Options'},
              {ws:'customer', ic:'👤', lb:'Customer Profile'},
              {ws:'promo',    ic:'🏷', lb:'Promo & Vouchers'},
            ].map(t => (
              <button key={t.ws}
                className={activeWs === t.ws ? 'active' : ''}
                onClick={() => setActiveWs(t.ws)}
                data-testid={`fc-tab-${t.ws}`}>
                {t.ic} <span className="lbl-txt">{t.lb}</span>
              </button>
            ))}
          </div>

          <div className="fpos-ws-panel">

            {/* ── PRODUCTS (3-step) ── */}
            {activeWs === 'products' && (
              <div className="fpos-ws-content" id="wsProducts">

                {/* STEP 1: SERVICE TYPE */}
                {productStep === 'service' && (
                  <div className="fpos-step-panel" id="stepService">
                    <div className="fpos-step-title">Select service type</div>
                    <div className="fpos-service-cards">
                      <div className="fpos-svc-card" onClick={() => selectServiceType('DineIn')} data-testid="svc-dinein">
                        <div className="fpos-svc-ic">🍽</div>
                        <div className="fpos-svc-lb">Dine In</div>
                        <div className="fpos-svc-sub">Seated at a table</div>
                      </div>
                      <div className="fpos-svc-card" onClick={() => selectServiceType('TakeAway')} data-testid="svc-takeaway">
                        <div className="fpos-svc-ic">🥡</div>
                        <div className="fpos-svc-lb">Take Away</div>
                        <div className="fpos-svc-sub">Pickup counter order</div>
                      </div>
                      <div className="fpos-svc-card" onClick={() => selectServiceType('Special')} data-testid="svc-special">
                        <div className="fpos-svc-ic">⭐</div>
                        <div className="fpos-svc-lb">Special Section</div>
                        <div className="fpos-svc-sub">Banquet / party order</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2: TABLE MAP */}
                {productStep === 'table' && (
                  <div className="fpos-step-panel" id="stepTable">
                    <div className="fpos-step-title-row">
                      <button className="fpos-back-link" onClick={() => goToStep('service')}>← Service type</button>
                      <div className="fpos-step-title">Select a table</div>
                      <div className="fpos-table-legend">
                        <span className="fpos-leg available">● Available</span>
                        <span className="fpos-leg occupied">● Occupied</span>
                        <span className="fpos-leg reserved">● Reserved</span>
                      </div>
                    </div>
                    <div className="fpos-table-grid" data-testid="table-grid">
                      {TABLES.map(t => (
                        <div key={t.no}
                          className={`fpos-table-tile ${t.status}`}
                          onClick={() => selectTable(t)}
                          data-testid={`table-${t.no}`}>
                          <div className="fpos-table-ic">🪑</div>
                          <div className="fpos-table-lb">Table {t.no}</div>
                          <div className="fpos-table-st">{t.status}</div>
                          {t.status === 'occupied' && <div className="fpos-table-amt">₹{((t.no * 137) % 900 + 200)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 3: ITEMS */}
                {productStep === 'items' && (
                  <div className="fpos-step-panel fpos-items-step" id="stepItems">
                    <div className="fpos-prod-head">
                      <div className="fpos-prod-head-left">
                        <button className="fpos-back-link light"
                          onClick={() => goToStep(serviceType === 'DineIn' ? 'table' : 'service')}>←</button>
                        <span className="fpos-svc-badge">{svcBadge}</span>
                      </div>
                      <input
                        className="fpos-item-search"
                        id="itemSearch"
                        placeholder="Search item, category, or code…"
                        value={searchQ}
                        onChange={e => setSearchQ(e.target.value)}
                        data-testid="fc-item-search"
                      />
                    </div>

                    <div className="fpos-cat-tabs">
                      <button className="fpos-arrow on">‹</button>
                      <div className="fpos-cat-strip">
                        {FOOD_CATS.map(c => (
                          <button key={c}
                            className={activeCat === c ? 'active' : ''}
                            onClick={() => { setActiveCat(c); setSearchQ(''); }}
                            data-testid={`fc-cat-${c}`}
                          >{c}</button>
                        ))}
                      </div>
                      <button className="fpos-arrow">›</button>
                    </div>

                    <div className="fpos-tile-grid" data-testid="fc-tile-grid">
                      {tiles.map((it, i) => (
                        <div key={i} className="fpos-tile"
                          onClick={() => addToOrder(it.n, it.p)}
                          data-testid={`fc-tile-${it.n.replace(/\s+/g,'-')}`}>
                          <div className="fpos-tile-sw">{it.e}</div>
                          <div className="fpos-tile-nm">{it.n}</div>
                          <div className="fpos-tile-pr fpos-mono">₹{it.p}</div>
                        </div>
                      ))}
                      {tiles.length === 0 && (
                        <div style={{gridColumn:'1/-1',textAlign:'center',color:'var(--ink-soft)',padding:'30px'}}>
                          No items match "{searchQ}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── PAYMENT ── */}
            {activeWs === 'payment' && (
              <div className="fpos-pay-body">
                <div className="fpos-pay-due">
                  <span className="fpos-lbl">Amount Due</span>
                  <span className="fpos-amt fpos-mono" data-testid="fc-pay-due">₹{fmt(rounded)}</span>
                </div>

                <div className="fpos-pay-methods">
                  {PAY_METHODS.map(m => (
                    <div key={m.id}
                      className={`fpos-pm-card${payMethod===m.id?' active':''}`}
                      onClick={() => { setPayMethod(m.id); showToast(m.lb + ' selected'); }}
                      data-testid={`fc-pay-${m.id}`}>
                      <div className="fpos-pm-ic">{m.ic}</div>
                      <div className="fpos-pm-lb">{m.lb}</div>
                    </div>
                  ))}
                </div>

                <div className="fpos-tender-row">
                  <div className="fpos-tender-box">
                    <label>Tendered Amount</label>
                    <input className="fpos-mono" type="number" placeholder="0.00"
                      value={tendered} onChange={e => setTendered(e.target.value)}
                      data-testid="fc-tendered" />
                  </div>
                  <div className="fpos-tender-box">
                    <label>Change Due</label>
                    <input className="fpos-mono" value={fmt(change)} readOnly data-testid="fc-change" />
                  </div>
                </div>

                <div className="fpos-quick-amt">
                  {quickAmts.map(v => (
                    <button key={v} onClick={() => setTendered(String(v))}>₹{fmt(v)}</button>
                  ))}
                </div>

                <button className="fpos-confirm-pay" onClick={confirmPayment} data-testid="fc-confirm-pay">
                  Confirm Payment
                </button>
              </div>
            )}

            {/* ── CUSTOMER ── */}
            {activeWs === 'customer' && (
              <div className="fpos-cust-body">
                <div className="fpos-cust-search">
                  <input placeholder="Search by name or mobile..."
                    value={custSearch} onChange={e => setCustSearch(e.target.value)} />
                </div>
                <div className="fpos-cust-list">
                  {filtCusts.map((c, i) => (
                    <div key={i} className="fpos-cust-card">
                      <div>
                        <span className="fpos-cust-nm">{c.name}</span>
                        <span className="fpos-cust-tier">{c.tier}</span>
                        <div className="fpos-cust-ph fpos-mono">{c.mobile}</div>
                      </div>
                      <button onClick={() => {
                        setCustName(c.name); setCustMobile(c.mobile);
                        showToast(c.name + ' loaded to bill');
                        setActiveWs('products');
                      }}>Select</button>
                    </div>
                  ))}
                </div>
                <div className="fpos-new-cust">
                  <h3>+ New Customer</h3>
                  <div className="fpos-new-grid">
                    <input placeholder="Full name" />
                    <input placeholder="Mobile number" className="fpos-mono" />
                  </div>
                  <button onClick={() => showToast('Customer saved')}>Save Customer</button>
                </div>
              </div>
            )}

            {/* ── PROMO ── */}
            {activeWs === 'promo' && (
              <div className="fpos-promo-body">
                <div className={`fpos-promo-active${promo ? ' show' : ''}`}>
                  <span className="fpos-pa-l">✓ <b>{promo?.code}</b> applied</span>
                  <button onClick={() => { setPromo(null); showToast('Promo removed'); }}>Remove</button>
                </div>
                <div className="fpos-promo-list">
                  {PROMOS.map(p => (
                    <div key={p.code} className="fpos-promo-card">
                      <div>
                        <span className="fpos-promo-code">{p.code}</span>
                        <div className="fpos-promo-desc">{p.desc}</div>
                      </div>
                      <button onClick={() => { setPromo(p); showToast(p.code + ' applied'); }}>Apply</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ════ RIGHT: FUNCTION RAIL ════════════════════════════════════ */}
        <div className="fpos-func-rail">
          <div className="fpos-fr-btn" onClick={() => showToast('More options…')}><div className="fpos-fr-ic">⋮</div><div className="fpos-fr-lb">More</div></div>
          <div className="fpos-fr-btn" onClick={() => showToast('Opening Food Court Billing')}><div className="fpos-fr-ic">🍴</div><div className="fpos-fr-lb">Food Court Billing</div></div>
          <div className="fpos-fr-btn" onClick={() => showToast('Printing KOT')}><div className="fpos-fr-ic">🖨</div><div className="fpos-fr-lb">KOT Billing</div></div>
          <div className="fpos-fr-btn" onClick={() => showToast('Opening reserved tables')}><div className="fpos-fr-ic">📂</div><div className="fpos-fr-lb">Resd Table</div></div>
          <div className="fpos-fr-btn" onClick={() => showToast('Reservation cancelled')}><div className="fpos-fr-ic">🗑</div><div className="fpos-fr-lb">Cancel Resd</div></div>
          <div className="fpos-fr-btn" onClick={() => showToast('Shifting table…')}><div className="fpos-fr-ic">🔀</div><div className="fpos-fr-lb">Shift Table</div></div>
          <div className="fpos-fr-btn save" onClick={handleSave} data-testid="fc-save"><div className="fpos-fr-ic">💾</div><div className="fpos-fr-lb">Save (F10)</div></div>
          <div className="fpos-fr-btn" onClick={handleClear}><div className="fpos-fr-ic">🔄</div><div className="fpos-fr-lb">Clear</div></div>
          <div className="fpos-fr-btn exit" onClick={() => navigateTo && navigateTo('HUB')}><div className="fpos-fr-ic">⏻</div><div className="fpos-fr-lb">Exit</div></div>
        </div>
      </div>

      {/* ── STATUS BAR ────────────────────────────────────────────────── */}
      <div className="fpos-statusbar">
        <span>Organization: <b>MyAccounts</b></span>
        <span>ScreenId <b>6.16.14</b></span>
        <span>Caption: <b>Point of Sale</b></span>
        <span>User: <b>Admin</b> <span className="fpos-mono">{clock}</span></span>
        <span>Business Day: <b className="fpos-mono">{todayStr()}</b></span>
        <span>Fin. Year: <b>2025–2026</b></span>
        <div className="fpos-sb-spacer">
          <div className="fpos-sb-icon">?</div>
          <div className="fpos-sb-icon">💬</div>
          <div className="fpos-sb-icon">📅</div>
        </div>
      </div>

      <div className={`fpos-toast${toastOn ? ' show' : ''}`}>{toastMsg}</div>
    </div>
  );
}
